// Bandeja del proveedor en la planta seleccionada (PP-34): las ofertas vivas
// de la empresa y los viajes que ya acepto o asigno, con el reloj que le queda
// a cada uno. Cierra el circuito que hoy corre por WhatsApp: aceptar o
// rechazar con motivo, y asignar vehiculo y conductor de la nomina.
//
// Reglas que esta vista respeta:
// - El reloj se calcula contra `deadlineAt` del servidor; al llegar a cero la
//   bandeja pregunta y muestra lo que diga el servidor (useInbox.ts).
// - La UI deshabilita, la API decide: todo 409 se explica en castellano simple
//   y la bandeja se vuelve a pedir.
// - Nunca se dice a que proveedor paso un viaje: solo que salio de la bandeja.
// - Las ofertas vivas aparecen sean del dia que sean (PP-34b). "Hoy" y
//   "Mañana" eligen solo que viajes ya asignados se listan debajo.
//
// Rediseño (docs/PLAN-REDISENO-PORTAL.md §6.4): el resultado de cada accion
// va en un toast; lo que pide decision (un viaje que salio de la bandeja)
// sigue en una fila de aviso fija.
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { Driver, OfferRejectReason, ProviderTrip, SiteVehicle } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import { Card, PanelHeader } from "../components/ui/card.js";
import { cn } from "../components/ui/cn.js";
import { EmptyState } from "../components/ui/empty.js";
import { Failure } from "../components/ui/failure.js";
import { Segmented } from "../components/ui/segmented.js";
import { SkeletonRows } from "../components/ui/skeleton.js";
import { useToast, type ToastTone } from "../components/ui/toast.js";
import type { FleetPort, FleetSite } from "../fleet/port.js";
import { useRoster } from "../fleet/useRoster.js";
import { StaleTag } from "../home/StaleTag.js";
import { eligibilitySummary } from "../logic/documents.js";
import { lastDriverByVehicle } from "../logic/quick-assign.js";
import { formatDate, nextDate, pendingCount, sortInbox, tripTitle } from "../logic/requests.js";
import type { SiteAccess } from "../sites/port.js";
import { systemClock, type Clock } from "./clock.js";
import { ACTION_ERROR_COPY } from "./labels.js";
import { RequestsError, type RequestsPort } from "./port.js";
import { TripCard, type AssignedDetail } from "./TripCard.js";
import { useInbox } from "./useInbox.js";

interface Props {
  session: PortalSession;
  port: RequestsPort;
  fleetPort: FleetPort;
  site: SiteAccess;
  /** Sin `onBack` no hay boton de volver: el shell del portal navega por la barra superior. */
  onBack?: () => void;
  clock?: Clock;
  /** PP-41: hoja de ruta del viaje asignado; la enchufa la seccion Viajes. */
  assignedDetail?: AssignedDetail;
}

type Day = "today" | "tomorrow";

interface Outcome {
  tone: ToastTone;
  text: string;
}

export function Requests({ session, port, fleetPort, site, onBack, clock = systemClock, assignedDetail }: Props): React.JSX.Element {
  const baseId = useId();
  const { notify } = useToast();
  const [day, setDay] = useState<Day>("today");
  const [today, setToday] = useState<string | undefined>(undefined);
  const [departed, setDeparted] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const { siteId, providerId } = site;
  const fleetSite: FleetSite = useMemo(() => ({ siteId, providerId }), [siteId, providerId]);
  const requestsSite = fleetSite;

  const date = day === "tomorrow" && today !== undefined ? nextDate(today) : undefined;
  const inbox = useInbox(port, session, requestsSite, date, clock);
  // PB-29: los mensajes nombran el viaje como la tarjeta, nunca por su id.
  const timezone = inbox.state.estado === "listo" ? inbox.state.timezone : undefined;

  const loadVehicles = useCallback((s: PortalSession, target: FleetSite) => fleetPort.listVehicles(s, target), [fleetPort]);
  const loadDrivers = useCallback((s: PortalSession, target: FleetSite) => fleetPort.listDrivers(s, target), [fleetPort]);
  const vehicles = useRoster<SiteVehicle>(loadVehicles, session, fleetSite);
  const drivers = useRoster<Driver>(loadDrivers, session, fleetSite);

  // El "hoy" lo fija la planta en la respuesta sin fecha, no el navegador.
  // Se ajusta en el render (patron de estado derivado de React), sin esperar
  // a un efecto: el boton Mañana queda habilitado en el mismo commit.
  if (inbox.state.estado === "listo" && day === "today" && inbox.state.date !== today) setToday(inbox.state.date);

  // Un viaje con oferta viva que deja de venir sale de la bandeja: vencio,
  // paso a otro proveedor o la planta lo cancelo. Se avisa sin decir a quien.
  // Los que la empresa rechazo recien no se avisan: ya se dijo.
  const previous = useRef<{ date: string; trips: readonly ProviderTrip[] } | undefined>(undefined);
  const rejectedByUs = useRef(new Set<string>());
  useEffect(() => {
    if (inbox.state.estado !== "listo") return;
    const before = previous.current;
    const { date: current, items } = inbox.state;
    previous.current = { date: current, trips: items };
    if (before === undefined || before.date !== current) return;
    const still = new Set(items.map((t) => t.tripId));
    const gone = before.trips
      .filter((t) => t.offer !== undefined && !still.has(t.tripId) && !rejectedByUs.current.has(t.tripId))
      .map((t) => t.tripId);
    if (gone.length > 0) setDeparted((list) => [...list.filter((id) => !gone.includes(id)), ...gone]);
  }, [inbox.state]);

  useEffect(() => {
    previous.current = undefined;
    setDeparted([]);
  }, [day, siteId, providerId]);

  const refreshInbox = inbox.refresh;
  const withBusy = useCallback(
    async (tripId: string, operation: () => Promise<Outcome>): Promise<boolean> => {
      setBusy((set) => new Set(set).add(tripId));
      try {
        const result = await operation();
        notify(result.text, result.tone);
        refreshInbox();
        return true;
      } catch (error: unknown) {
        const kind = error instanceof RequestsError ? error.kind : "unavailable";
        notify(ACTION_ERROR_COPY[kind], "error");
        // Salvo una caida de red o de la API, el estado cambio: se vuelve a pedir.
        if (kind !== "unavailable") refreshInbox();
        return false;
      } finally {
        setBusy((set) => {
          const next = new Set(set);
          next.delete(tripId);
          return next;
        });
      }
    },
    [notify, refreshInbox],
  );

  const onAccept = useCallback(
    (trip: ProviderTrip, offerId: number) =>
      withBusy(trip.tripId, async () => {
        await port.accept(session, requestsSite, trip.tripId, offerId);
        return { tone: "ok", text: `Oferta aceptada: ${tripTitle(trip, timezone)}. Falta asignar vehículo y conductor antes de que venza el tiempo.` };
      }),
    [withBusy, port, session, requestsSite, timezone],
  );

  const onReject = useCallback(
    (trip: ProviderTrip, offerId: number, reason: OfferRejectReason) =>
      withBusy(trip.tripId, async () => {
        await port.reject(session, requestsSite, trip.tripId, offerId, reason);
        rejectedByUs.current.add(trip.tripId);
        return { tone: "info", text: `Oferta rechazada: ${tripTitle(trip, timezone)}. El viaje sale de la bandeja de la empresa.` };
      }),
    [withBusy, port, session, requestsSite, timezone],
  );

  const onAssign = useCallback(
    (trip: ProviderTrip, vehicleId: string, driverId: string) =>
      withBusy(trip.tripId, async () => {
        const result = await port.assign(session, requestsSite, trip.tripId, { by: "id", vehicleId, driverId });
        if (result.eligible) {
          return { tone: "ok", text: `Vehículo y conductor asignados: ${tripTitle(trip, timezone)}.` };
        }
        const reasons = [
          result.eligibility.vehicle.eligible ? undefined : `vehículo: ${eligibilitySummary(result.eligibility.vehicle).detail}`,
          result.eligibility.driver.eligible ? undefined : `conductor: ${eligibilitySummary(result.eligibility.driver).detail}`,
        ].filter((line): line is string => line !== undefined);
        return {
          tone: "aviso",
          text: `Asignado con advertencia: ${tripTitle(trip, timezone)}. No elegible en esta planta (${reasons.join("; ")}). La planta recibe la alerta.`,
        };
      }),
    [withBusy, port, session, requestsSite, timezone],
  );

  const state = inbox.state;
  const trips = state.estado === "listo" ? sortInbox(state.items) : [];
  const pending = state.estado === "listo" ? pendingCount(state.items, inbox.now) : 0;
  // PP-65: conductor propuesto al asignar por codigo, de los viajes que la
  // bandeja ya tiene (sin otra lectura ni columna; ver logic/quick-assign.ts).
  const items = state.estado === "listo" ? state.items : undefined;
  const lastDrivers = useMemo(() => (items === undefined ? new Map<string, string>() : lastDriverByVehicle(items, providerId)), [items, providerId]);

  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="m-0 text-eyebrow font-semibold uppercase tracking-eyebrow text-acero-texto">{site.name}</p>
          <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
            Bandeja
          </h1>
          <p className="m-0 text-xs text-texto-suave">
            {state.estado === "listo"
              ? `Ofertas por responder o asignar de cualquier día, y viajes de la empresa del ${formatDate(state.date)} (fecha operativa de la planta). Se actualiza sola.`
              : "Ofertas y viajes de la empresa en esta planta."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented<Day>
            label="Día de los viajes asignados"
            value={day}
            options={[
              { value: "today", label: "Hoy" },
              { value: "tomorrow", label: "Mañana", disabled: today === undefined },
            ]}
            onChange={setDay}
          />
          {onBack !== undefined && (
            <Button variant="secundario" size="sm" onClick={onBack}>
              Volver a plantas
            </Button>
          )}
        </div>
      </div>

      {departed.length > 0 && (
        <Alert variant="aviso" className="flex flex-wrap items-start justify-between gap-2">
          <span>
            {departed.length === 1
              ? `El viaje ${departed[0] ?? ""} salió de la bandeja: se venció el tiempo y pasó a otro proveedor, o la planta lo canceló.`
              : `Los viajes ${departed.join(", ")} salieron de la bandeja: se venció el tiempo y pasaron a otro proveedor, o la planta los canceló.`}
          </span>
          <Button variant="enlace" size="inline" onClick={() => setDeparted([])}>
            Cerrar aviso
          </Button>
        </Alert>
      )}

      <Card className="gap-0 p-0">
        <PanelHeader title="Viajes" count={state.estado === "listo" ? trips.length : undefined}>
          {state.estado === "listo" && state.stale && <StaleTag />}
          <Button variant="enlace" size="inline" className="text-xs" onClick={inbox.refresh}>
            Actualizar ahora
          </Button>
        </PanelHeader>
        {state.estado === "cargando" && <SkeletonRows rows={3} label="Consultando la bandeja" />}
        {state.estado === "denegado" && (
          <div className="p-3.5">
            <Failure title="La cuenta no tiene acceso a la bandeja de esta planta.">Salir e ingresar con una cuenta de operador de proveedor.</Failure>
          </div>
        )}
        {state.estado === "error" && (
          <div className="p-3.5">
            <Failure title="No se pudo consultar la bandeja. Se reintenta sola en unos segundos." onRetry={inbox.refresh} />
          </div>
        )}
        {state.estado === "listo" && (
          <>
            <p role="status" className={cn("m-0 border-b border-divisor px-3.5 py-2 text-sm", pending > 0 ? "font-semibold text-texto" : "text-texto-suave")}>
              {pending === 0
                ? "Nada pendiente de respuesta."
                : pending === 1
                  ? "1 viaje espera respuesta o asignación."
                  : `${String(pending)} viajes esperan respuesta o asignación.`}
            </p>
            {state.stale && (
              <Alert variant="aviso" className="m-3.5">
                No se pudo actualizar la bandeja. Se muestra lo último recibido y se reintenta sola.
              </Alert>
            )}
            {state.truncated && (
              <Alert variant="aviso" className="m-3.5">
                La bandeja tiene más viajes de los que se muestran. Revisar con UCORP.
              </Alert>
            )}
            {trips.length === 0 ? (
              <EmptyState title="No hay ofertas abiertas ni viajes asignados a la empresa para este día.">
                Cuando la planta ofrezca un viaje, de cualquier día, aparece acá con el tiempo para responder.
              </EmptyState>
            ) : (
              <ul aria-label="Viajes" className="m-0 list-none p-0">
                {trips.map((trip) => (
                  <TripCard
                    key={trip.tripId}
                    trip={trip}
                    now={inbox.now}
                    inboxDate={state.date}
                    timezone={state.timezone}
                    busy={busy.has(trip.tripId)}
                    vehicles={vehicles.state}
                    drivers={drivers.state}
                    session={session}
                    port={port}
                    site={requestsSite}
                    siteName={site.name}
                    lastDrivers={lastDrivers}
                    onAccept={onAccept}
                    onReject={onReject}
                    onAssign={onAssign}
                    {...(assignedDetail !== undefined ? { assignedDetail } : {})}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </Card>
    </section>
  );
}
